import { uniq } from 'es-toolkit';

import { LayoutFeatureArea, Point2D as Point2DInterface } from '@paragon/contract-library';
import { point2DTo3D } from '@paragon/geometry-library';
import { enqueueError } from '@paragon/notification-tools';
import { assertExhaustiveSwitch, notNullish } from '@paragon/utilities';

import { SerializedEntitySnapTarget } from '../../../library/layout-snapping/EntitySnapTarget';
import {
  transformBearingEnvelopes,
  transformCeilingTrays,
  transformFeatureAreas,
  transformFeaturePatternContainers,
  transformPlanes,
  transformTrussEnvelopes,
  transformWorkLines,
} from '../../../library/transformations/application';
import { duplicateTransformableObjects } from '../../../library/transformations/duplication';
import { prepareTransformation } from '../../../library/transformations/preparation';
import {
  getCurrentTransformationTargets,
  isCurrentSelectionTransformable,
} from '../../../library/transformations/selectors';
import { ITransformation, ITransformer, TransformationType } from '../../../library/transformations/types';
import { isTransformationFullyDefined } from '../../../library/transformations/validation';
import {
  createGetLevelGuids,
  getAllBearingEnvelopes,
  getAllBearingEnvelopesDictionary,
  getAllFeaturePatternContainers,
  getAllFeaturePatternContainersDictionary,
  getAllTrussEnvelopes,
  getAllWorkLines,
  getLevelGuidsAffectedByWorkLineEdit,
} from '../../selectors/layoutDataSelectors';
import {
  getBoundedRoofAndCeilingPlaneShapesByGuid,
  getCalculatedRoofAndCeilingPlanes,
} from '../../selectors/roofAndCeilingPlaneSelectors';
import {
  transformationBeganOrUpdated,
  transformationCanceledOrEnded,
  transformationFeaturePatternContainerGuidSet,
  transformationLevelReassignmentEnabled,
} from '../../slices/applicationSlice';
import { layoutSlice } from '../../slices/layoutSlice';
import { selectItemsByGuid } from '../selectActions';
import { AsyncThunkActionVoid, ThunkActionVoid } from '../thunkActions';
import { editBearingEnvelopes } from './bearingEnvelopeActions';
import { editCeilingPlanes } from './ceilingPlaneActions';
import { editCeilingTrays } from './ceilingTrayActions';
import { editFeatureAreas } from './featureAreaActions';
import { editFeaturePatternContainers } from './featurePatternContainerActions';
import { editRoofPlanes } from './roofPlaneActions';
import { saveTrussEnvelopes } from './trussEnvelopeActions';
import { saveWorkLines } from './workLineActions';

function getDefaultDuplicationSetting(type: TransformationType): boolean {
  switch (type) {
    case 'movement':
    case 'rotation':
      return false;

    case 'mirror':
      return true;

    default:
      assertExhaustiveSwitch(type);
  }
}

export const beginTransformation =
  (type: TransformationType, options?: { duplicate: boolean }): ThunkActionVoid =>
  (dispatch, getState) => {
    const state = getState();
    if (!isCurrentSelectionTransformable(state)) return;

    const duplicate = options?.duplicate ?? getDefaultDuplicationSetting(type);

    switch (type) {
      case 'movement':
        dispatch(transformationBeganOrUpdated({ type: 'movement', duplicate, reassign: false }));
        break;

      case 'rotation':
        dispatch(transformationBeganOrUpdated({ type: 'rotation', angle: 0, duplicate, reassign: false }));
        break;

      case 'mirror':
        dispatch(transformationBeganOrUpdated({ type: 'mirror', duplicate, reassign: false }));
        break;

      default:
        assertExhaustiveSwitch(type);
    }
  };

export const enableTransformationLevelReassignment = (): ThunkActionVoid => (dispatch, getState) => {
  const state = getState();
  const targets = getCurrentTransformationTargets(state);

  if (targets == null || targets.type === 'workLine') return;

  const relevantLevelGuids = createGetLevelGuids(state)(targets.entities);

  if (relevantLevelGuids.length === 1) {
    dispatch(transformationLevelReassignmentEnabled({ levelGuid: relevantLevelGuids[0] }));
  } else {
    throw new Error('Object being transformed belong to multiple or no levels; reassignment is not possible.');
  }

  if (
    targets.type === 'trussEnvelope' &&
    targets.entities.some(({ definitionType }) => definitionType === 'FeaturePattern')
  ) {
    const featurePatternContainerGuid =
      uniq(
        targets.entities.map(({ featurePatternContainerGuid }) => featurePatternContainerGuid).filter(notNullish),
      )[0] ?? null;
    dispatch(transformationFeaturePatternContainerGuidSet(featurePatternContainerGuid));
  }
};

export const setMovementOriginPoint =
  (point: Point2DInterface): ThunkActionVoid =>
  (dispatch, getState) => {
    const transformation = getState().application.transformingObjectsInfo;
    if (transformation?.type !== 'movement') return;

    dispatch(transformationBeganOrUpdated({ ...transformation, origin: point2DTo3D(point) }));
  };

export const setMovementDestinationPoint =
  (point: Point2DInterface): ThunkActionVoid =>
  (dispatch, getState) => {
    const transformation = getState().application.transformingObjectsInfo;
    if (transformation?.type !== 'movement') return;

    dispatch(transformationBeganOrUpdated({ ...transformation, destination: point2DTo3D(point) }));
  };

export const setMirrorCenterLine =
  (line: SerializedEntitySnapTarget): ThunkActionVoid =>
  (dispatch, getState) => {
    const transformation = getState().application.transformingObjectsInfo;
    if (transformation?.type !== 'mirror') return;

    dispatch(transformationBeganOrUpdated({ ...transformation, center: line }));
  };

export const applyPendingTransformationIfFullyDefined = (): AsyncThunkActionVoid => async (dispatch, getState) => {
  const transformation = getState().application.transformingObjectsInfo;
  if (!isTransformationFullyDefined(transformation)) return;

  await dispatch(applyExplicitTransformation(transformation));
};

export const applyExplicitTransformation =
  (transformation: ITransformation): AsyncThunkActionVoid =>
  async (dispatch) => {
    if (!isTransformationFullyDefined(transformation)) return;

    const transformer = prepareTransformation(transformation);
    await dispatch(
      applyTransformerToSelectedObjects(transformer, {
        duplicate: transformation.duplicate ?? false,
        preserveLinkedComponents: transformation.preserveLinkedComponents ?? false,
      }),
    );
  };

export const applyTransformerToSelectedObjects =
  (
    transformer: ITransformer,
    options: { duplicate: boolean; preserveLinkedComponents: boolean },
  ): AsyncThunkActionVoid =>
  async (dispatch, getState) => {
    const state = getState();
    const targets = getCurrentTransformationTargets(state);

    if (targets == null) return;

    // needed for transforming duplicated feature areas
    const guidMapping = new Map<string, string>();

    if (options.duplicate) {
      let renamingOptions = null;

      switch (targets.type) {
        case 'trussEnvelope':
          renamingOptions = { takenNames: getAllTrussEnvelopes(state).map((envelope) => envelope.name) };
          break;

        case 'beam':
        case 'wall':
          renamingOptions = { takenNames: getAllBearingEnvelopes(state).map((envelope) => envelope.name) };
          break;

        case 'workLine':
          renamingOptions = { prefix: 'WL', takenNames: getAllWorkLines(state).map((line) => line.name) };
          break;

        case 'featurePatternContainer':
          renamingOptions = { takenNames: getAllFeaturePatternContainers(state).map((container) => container.name) };
          break;

        default:
          if (targets.entities.some((entity) => 'name' in entity)) {
            throw new Error(`Unsupported type for duplication: did not expect ${targets.type} to have names.`);
          }
      }

      // TODO: typescript
      targets.entities = duplicateTransformableObjects(targets.type, targets.entities, {
        guidMapping,
        rename: renamingOptions,
        preserveLinkedComponents: options.preserveLinkedComponents,
      }) as unknown as any;
    }

    try {
      switch (targets.type) {
        case 'trussEnvelope':
          const dependencies = { roofAndCeilingPlanes: getCalculatedRoofAndCeilingPlanes(state) };
          await dispatch(
            saveTrussEnvelopes(
              transformTrussEnvelopes(targets.entities, transformer, dependencies, {
                preserveLinkedComponents: options.preserveLinkedComponents,
              }),
            ),
          );
          break;

        case 'beam':
        case 'wall':
          const changes = transformBearingEnvelopes(targets.entities, transformer);

          if (options.duplicate) {
            await dispatch(editBearingEnvelopes(changes, { createdEnvelopes: targets.entities }));
          } else {
            await dispatch(editBearingEnvelopes(changes));
          }

          break;

        case 'workLine': {
          const updatedWorkLines = transformWorkLines(targets.entities, transformer);
          const affectedLevelGuids = getLevelGuidsAffectedByWorkLineEdit(
            getState(),
            [],
            updatedWorkLines.map((workLine) => workLine.scope),
          );
          dispatch(layoutSlice.actions.workLinesUpserted({ workLines: updatedWorkLines, affectedLevelGuids }));
          await dispatch(saveWorkLines());
          break;
        }

        case 'roofPlane': {
          if (options.duplicate) dispatch(layoutSlice.actions.roofPlanesUpserted(targets.entities));

          const dependencies = {
            bearingEnvelopes: getAllBearingEnvelopesDictionary(state),
            planes: getBoundedRoofAndCeilingPlaneShapesByGuid(state),
          };
          const editInstructions = transformPlanes(targets.entities, transformer, dependencies);

          if (options.duplicate) {
            await dispatch(editRoofPlanes(editInstructions, { createdPlanes: targets.entities }));
          } else {
            await dispatch(editRoofPlanes(editInstructions));
          }

          break;
        }

        case 'ceilingPlane': {
          if (options.duplicate) dispatch(layoutSlice.actions.ceilingPlanesUpserted(targets.entities));

          const dependencies = {
            bearingEnvelopes: getAllBearingEnvelopesDictionary(state),
            planes: getBoundedRoofAndCeilingPlaneShapesByGuid(state),
          };
          const editInstructions = transformPlanes(targets.entities, transformer, dependencies);

          if (options.duplicate) {
            await dispatch(editCeilingPlanes(editInstructions, { createdPlanes: targets.entities }));
          } else {
            await dispatch(editCeilingPlanes(editInstructions));
          }

          break;
        }

        case 'ceilingTray': {
          const editInstructions = transformCeilingTrays(targets.entities, transformer);

          if (options.duplicate) {
            await dispatch(editCeilingTrays(editInstructions, { createdTrays: targets.entities }));
          } else {
            await dispatch(editCeilingTrays(editInstructions));
          }

          break;
        }

        case 'featureArea': {
          const dependencies = {
            guidMapping,
            featurePatternContainers: getAllFeaturePatternContainersDictionary(state),
          };

          const editInstructions = transformFeatureAreas(targets.entities, transformer, dependencies);

          if (options.duplicate) {
            const createdAreas = targets.entities.reduce(
              (areas, area) => {
                const originalAreaGuid = dependencies.guidMapping.get(area.guid)!;
                const containerGuid = Object.values(dependencies.featurePatternContainers).find((container) =>
                  container.featureAreas.some((existingArea) => existingArea.guid === originalAreaGuid),
                );

                if (containerGuid) {
                  areas[containerGuid.guid] = areas[containerGuid.guid] || [];
                  areas[containerGuid.guid].push(area);
                }

                return areas;
              },
              {} as { [containerGuid: string]: LayoutFeatureArea[] },
            );

            await dispatch(editFeatureAreas(editInstructions, { createdAreas }));
          } else {
            await dispatch(editFeatureAreas(editInstructions));
          }

          break;
        }

        case 'featurePatternContainer': {
          const dependencies = { bearingEnvelopes: getAllBearingEnvelopesDictionary(state) };
          const changes = transformFeaturePatternContainers(targets.entities, transformer, dependencies);

          if (options.duplicate) {
            await dispatch(editFeaturePatternContainers(changes, { createdContainers: targets.entities }));
          } else {
            await dispatch(editFeaturePatternContainers(changes));
          }

          break;
        }

        default:
          assertExhaustiveSwitch(targets);
      }
    } catch (error) {
      console.error(error);
      dispatch(enqueueError('Error moving selected objects. Please contact Paragon Customer Support.'));
      return;
    } finally {
      dispatch(endTransformation());
    }

    dispatch(
      selectItemsByGuid(
        targets.entities.map((entity) => entity.guid),
        targets.type,
        { intent: 'select', exclusive: true },
      ),
    );
  };

export const cancelTransformation = () => transformationCanceledOrEnded();
export const endTransformation = () => transformationCanceledOrEnded();
